import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, Loader2, Lock, MessagesSquare, Network, Pencil, Plus,
    Presentation as PresentationIcon, Send, Sparkles, Trash2, XCircle,
} from "lucide-react";
import { api, formatApiError, streamHubChat } from "../lib/api";

const STATUS_LABEL = { utkast: "Utkast – kräver granskning", godkand: "Godkänd", avvisad: "Avvisad", fel: "Generering misslyckades" };

function DraftCard({ draft, projectName, projects, onStatus, onApply }) {
    const navigate = useNavigate();
    const [chosenPid, setChosenPid] = useState("");
    const [applying, setApplying] = useState(false);
    let c = {};
    try { c = JSON.parse(draft.innehall); } catch { c = {}; }
    const apply = async () => {
        setApplying(true);
        try { await onApply(draft.id); } finally { setApplying(false); }
    };
    const nodeLabel = (ref) => (c.noder || []).find((n) => n.ref === ref)?.label || ref;
    if (draft.status === "fel") {
        return (
            <div data-testid={`hub-draft-card-${draft.id}`} className="rounded-xl border border-red-500/30 bg-red-500/5 p-3.5">
                <p className="text-xs text-red-400">Genereringen misslyckades – försök igen.</p>
            </div>
        );
    }
    return (
        <div data-testid={`hub-draft-card-${draft.id}`} className="rounded-xl border border-aidraft/30 bg-aidraft/5 p-3.5">
            <div className="flex items-center justify-between gap-2 mb-2">
                <span data-testid={`hub-draft-status-${draft.id}`} className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${draft.status === "utkast" ? "text-aidraft border-aidraft/40 bg-aidraft/10" : draft.status === "godkand" ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                    {STATUS_LABEL[draft.status] || draft.status}
                </span>
                <span className="font-mono text-[9px] text-slate-600">{projectName || "Alla projekt"}</span>
            </div>
            <h3 className="font-display font-bold text-slate-100">{c.titel || (draft.typ === "strukturforslag" ? "Strukturförslag" : "Presentationsutkast")}</h3>
            {draft.typ === "strukturforslag" ? (
                <>
                    {c.motivering && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{c.motivering}</p>}
                    <div className="mt-2.5 space-y-1.5">
                        {(c.noder || []).map((n, i) => (
                            <div key={i} className="flex items-center gap-2 rounded-lg bg-abyss/40 border border-border px-2.5 py-1.5">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-current shrink-0">{n.typ}</span>
                                <span className="text-xs text-slate-200">{n.label}</span>
                            </div>
                        ))}
                    </div>
                    {(c.relationer || []).length > 0 && (
                        <div className="mt-2 space-y-0.5">
                            {c.relationer.map((r, i) => (
                                <p key={i} className="text-[11px] text-slate-400">{nodeLabel(r.fran)} → {nodeLabel(r.till)}{r.label ? `: ${r.label}` : ""}</p>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <>
                    {c.huvudbudskap && <p className="text-xs text-slate-200 mt-1 leading-relaxed">{c.huvudbudskap}</p>}
                    <div className="mt-2.5 space-y-2.5">
                        {(c.sektioner || []).map((s, i) => (
                            <div key={i} className="rounded-lg bg-abyss/40 border border-border p-2.5">
                                <p className="text-xs font-semibold text-slate-200">{s.rubrik}</p>
                                {s.text && <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{s.text}</p>}
                                {(s.punkter || []).map((p, j) => <p key={j} className="text-[11px] text-slate-300 mt-0.5">· {p}</p>)}
                            </div>
                        ))}
                    </div>
                    {c.avslut && <p className="text-[11px] text-buoy mt-2.5 leading-relaxed">{c.avslut}</p>}
                </>
            )}
            {draft.status === "utkast" && (
                <div className="flex gap-2 mt-3">
                    <button data-testid={`hub-draft-approve-${draft.id}`} onClick={() => onStatus(draft.id, "godkand")}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-1.5 text-xs font-semibold hover:bg-buoy/25 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Godkänn
                    </button>
                    <button data-testid={`hub-draft-reject-${draft.id}`} onClick={() => onStatus(draft.id, "avvisad")}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-slate-600 text-slate-400 py-1.5 text-xs hover:border-red-500/50 hover:text-red-400 transition-colors">
                        <XCircle className="w-3.5 h-3.5" /> Avvisa
                    </button>
                </div>
            )}
            {draft.typ === "strukturforslag" && draft.status === "godkand" && !draft.applied_at && draft.project_id && (
                <button data-testid={`hub-draft-apply-${draft.id}`} onClick={apply} disabled={applying}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-1.5 text-xs font-semibold hover:bg-buoy/25 transition-colors disabled:opacity-50">
                    {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Network className="w-3.5 h-3.5" />}
                    Verkställ på kartan
                </button>
            )}
            {draft.typ === "strukturforslag" && draft.status === "godkand" && !draft.project_id && !draft.applied_at && (
                <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-widest text-slate-500">Saknar projektkoppling – kan inte verkställas</p>
            )}
            {draft.typ === "strukturforslag" && draft.applied_at && (
                <p data-testid={`hub-draft-applied-${draft.id}`} className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-buoy">Verkställd på kartan</p>
            )}
            {draft.typ !== "strukturforslag" && draft.status === "godkand" && draft.project_id && (
                <Link data-testid={`hub-draft-open-presentation-${draft.id}`} to={`/view/${draft.project_id}`}
                    className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-current/15 border border-current/40 text-current py-1.5 text-xs font-semibold hover:bg-current/25 transition-colors">
                    <PresentationIcon className="w-3.5 h-3.5" /> Öppna Executive Dashboard
                </Link>
            )}
            {draft.typ !== "strukturforslag" && draft.status === "godkand" && !draft.project_id && (
                <div className="mt-3 flex gap-1.5">
                    <select data-testid={`hub-draft-project-select-${draft.id}`} value={chosenPid} onChange={(e) => setChosenPid(e.target.value)}
                        className="flex-1 min-w-0 bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs text-slate-200 outline-none focus:border-current/60">
                        <option value="">Välj projekt …</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button data-testid={`hub-draft-open-presentation-${draft.id}`} disabled={!chosenPid}
                        onClick={() => navigate(`/view/${chosenPid}`)}
                        className="shrink-0 flex items-center gap-1.5 rounded-lg bg-current/15 border border-current/40 text-current px-2.5 py-1.5 text-xs font-semibold hover:bg-current/25 transition-colors disabled:opacity-40">
                        <PresentationIcon className="w-3.5 h-3.5" /> Öppna
                    </button>
                </div>
            )}
        </div>
    );
}

export default function ConsultantHub() {
    const [projects, setProjects] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [active, setActive] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [scope, setScope] = useState("");
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [creating, setCreating] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [projectsLoaded, setProjectsLoaded] = useState(false);
    const [showDraftsMobile, setShowDraftsMobile] = useState(false);
    const chatEndRef = useRef(null);
    const draftsRef = useRef([]);
    const activeIdRef = useRef(null);

    useEffect(() => { draftsRef.current = drafts; }, [drafts]);
    useEffect(() => { activeIdRef.current = active?.id || null; }, [active?.id]);

    useEffect(() => {
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => toast.error("Kunde inte ladda projekt")).finally(() => setProjectsLoaded(true));
        refreshConversations();
    }, []);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages]);

    const refreshConversations = async () => {
        try {
            const r = await api.get("/hub/conversations");
            setConversations(r.data);
            return r.data;
        } catch {
            toast.error("Kunde inte ladda konversationer");
            return [];
        }
    };

    const openConversation = async (cid) => {
        try {
            const r = await api.get(`/hub/conversations/${cid}`);
            setActive(r.data.conversation);
            setDrafts(r.data.drafts);
        } catch {
            toast.error("Kunde inte öppna konversationen");
        }
    };

    const createConversation = async () => {
        setCreating(true);
        try {
            const r = await api.post("/hub/conversations", { title: "Ny konversation", project_id: scope || null });
            await refreshConversations();
            await openConversation(r.data.id);
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skapa konversationen"));
        } finally {
            setCreating(false);
        }
    };

    const deleteConversation = async (cid) => {
        try {
            await api.delete(`/hub/conversations/${cid}`);
            if (active?.id === cid) { setActive(null); setDrafts([]); }
            refreshConversations();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const saveRename = async (cid) => {
        if (!renameValue.trim()) { setRenaming(null); return; }
        try {
            await api.put(`/hub/conversations/${cid}`, { title: renameValue.trim() });
            setRenaming(null);
            refreshConversations();
            if (active?.id === cid) setActive({ ...active, title: renameValue.trim() });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const appendMessage = (m) => setActive((prev) => prev ? { ...prev, messages: [...prev.messages, m] } : prev);

    const pollHub = (cid, baseMsgCount = null) => {
        const known = new Set(draftsRef.current.map((d) => d.id));
        const started = Date.now();
        const tick = async () => {
            if (activeIdRef.current !== cid) return;
            if (Date.now() - started > 150000) {
                toast.info("AI:n tar längre tid än väntat – resultatet dyker upp när du öppnar konversationen igen");
                return;
            }
            try {
                const r = await api.get(`/hub/conversations/${cid}`);
                const fresh = r.data.drafts.filter((d) => !known.has(d.id));
                const newMsgs = baseMsgCount !== null && (r.data.conversation.messages?.length || 0) > baseMsgCount;
                if (fresh.length || newMsgs) {
                    setDrafts(r.data.drafts);
                    if (fresh.length && fresh.every((d) => d.status === "fel")) {
                        toast.error("Genereringen misslyckades – försök igen");
                        return;
                    }
                    setActive(r.data.conversation);
                    toast.success(fresh.length ? "Utkast skapat – granska det i panelen" : "Klart – se svaret i chatten");
                    return;
                }
            } catch { /* fortsätt polla */ }
            setTimeout(tick, 4000);
        };
        setTimeout(tick, 4000);
    };

    const send = async () => {
        const msg = input.trim();
        if (!msg || streaming || !active) return;
        const cid = active.id;
        const baseMsgs = active.messages.length + 2;
        setInput("");
        appendMessage({ role: "user", content: msg });
        appendMessage({ role: "assistant", content: "" });
        setStreaming(true);
        try {
            await streamHubChat(cid, msg, {
                onDelta: (delta) => setActive((prev) => {
                    if (!prev) return prev;
                    const messages = [...prev.messages];
                    messages[messages.length - 1] = { role: "assistant", content: messages[messages.length - 1].content + delta };
                    return { ...prev, messages };
                }),
                onDraft: (draft) => {
                    setDrafts((prev) => [draft, ...prev]);
                    toast.success(draft.typ === "strukturforslag" ? "Strukturförslag skapat – granska det i panelen" : "Presentationsutkast skapat – granska det i panelen");
                },
                onDraftPending: (kind) => {
                    toast.info(kind === "strukturforslag" ? "Strukturförslag genereras i bakgrunden …" : "Presentationsutkast genereras i bakgrunden …");
                    pollHub(cid);
                },
                onActionPending: () => {
                    toast.info("AI:n utför arbetet i bakgrunden …");
                    pollHub(cid, baseMsgs);
                },
                onDraftError: (err) => toast.error(err),
                onIncomplete: () => {
                    toast.warning("Anslutningen bröts – hämtar det sparade svaret");
                    openConversation(cid);
                },
            });
            await refreshConversations();
            await openConversation(cid);
        } catch {
            toast.error("Chatten misslyckades – försök igen");
            setActive((prev) => prev ? { ...prev, messages: prev.messages.filter((m) => m.content !== "") } : prev);
        } finally {
            setStreaming(false);
        }
    };

    const generateDraft = async (kind = "presentation") => {
        if (!active || generating) return;
        setGenerating(kind);
        try {
            await api.post(`/hub/conversations/${active.id}/presentation-draft`, { kind });
            toast.info(kind === "strukturforslag" ? "Strukturförslag genereras i bakgrunden …" : "Presentationsutkast genereras i bakgrunden …");
            pollHub(active.id);
        } catch (e) {
            toast.error(formatApiError(e, "Utkastet kunde inte startas"));
        } finally {
            setGenerating(null);
        }
    };

    const applyStructure = async (did) => {
        try {
            const r = await api.post(`/drafts/${did}/apply-structure`);
            setDrafts((prev) => prev.map((d) => d.id === did ? { ...d, applied_at: new Date().toISOString() } : d));
            toast.success(`Strukturen verkställd: ${r.data.noder} noder och ${r.data.relationer} relationer tillagda på kartan`);
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte verkställa strukturen"));
        }
    };

    const setDraftStatus = async (did, status) => {
        try {
            await api.put(`/drafts/${did}`, { status });
            setDrafts((prev) => prev.map((d) => d.id === did ? { ...d, status } : d));
            toast.success(status === "godkand" ? "Utkast godkänt – syns nu i presentationsläget" : "Utkast avvisat");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const projectName = (pid) => projects.find((p) => p.id === pid)?.name;

    return (
        <div className="h-screen bg-abyss flex flex-col" data-testid="consultant-hub">
            <header className="h-14 shrink-0 bg-canvas/80 backdrop-blur-md border-b border-border flex items-center gap-3 px-4 z-50" data-testid="hub-header">
                <Link data-testid="hub-back-button" to="/" className="flex items-center gap-2 text-slate-400 hover:text-current transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="text-xs font-medium hidden sm:inline">Till kartan</span>
                </Link>
                <div className="flex items-center gap-2">
                    <img src="/hamn-emblem.png" alt="HAMN" className="w-7 h-7 rounded-full border border-current/30 object-cover" />
                    <span className="font-display font-extrabold tracking-tight text-slate-100">Consultant Hub</span>
                </div>
                <div className="flex-1" />
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500" data-testid="hub-lock-badge">
                    <Lock className="w-3 h-3" /> Låst till ditt konto
                </span>
            </header>

            <div className="flex-1 flex min-h-0">
                <aside className="w-60 xl:w-72 shrink-0 border-r border-border flex flex-col min-h-0" data-testid="hub-sidebar">
                    <div className="p-3 border-b border-border space-y-2">
                        <select data-testid="hub-scope-select" value={scope} onChange={(e) => setScope(e.target.value)}
                            disabled={!projectsLoaded}
                            className="w-full bg-abyss/60 border border-border rounded-lg px-2.5 py-2 text-xs text-slate-200 outline-none focus:border-current/60 disabled:opacity-50">
                            {!projectsLoaded && <option value="">Laddar projekt …</option>}
                            {projectsLoaded && <option value="">Alla projekt</option>}
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.demo ? " (demo)" : ""}</option>)}
                        </select>
                        <button data-testid="hub-new-conversation" onClick={createConversation} disabled={creating || !projectsLoaded}
                            className="w-full flex items-center justify-center gap-2 rounded-lg bg-current text-abyss px-3 py-2 text-xs font-semibold hover:bg-tide transition-colors disabled:opacity-50">
                            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            Ny konversation
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto panel-scroll p-2 space-y-1">
                        {conversations.length === 0 && (
                            <p className="text-xs text-slate-600 text-center py-6 px-3">Inga konversationer ännu. Välj omfattning och starta en ny.</p>
                        )}
                        {conversations.map((c) => (
                            <div key={c.id} data-testid={`hub-conversation-${c.id}`}
                                className={`group rounded-lg border px-2.5 py-2 cursor-pointer transition-colors ${active?.id === c.id ? "border-current/50 bg-current/10" : "border-transparent hover:bg-deepsea/60"}`}
                                onClick={() => renaming !== c.id && openConversation(c.id)}>
                                {renaming === c.id ? (
                                    <input data-testid={`hub-rename-input-${c.id}`} autoFocus value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(c.id); if (e.key === "Escape") setRenaming(null); }}
                                        onBlur={() => saveRename(c.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-full bg-abyss/80 border border-current/50 rounded px-1.5 py-0.5 text-xs text-slate-100 outline-none" />
                                ) : (
                                    <div className="flex items-center gap-1.5">
                                        <MessagesSquare className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                        <span className="flex-1 text-xs text-slate-300 truncate">{c.title}</span>
                                        <button data-testid={`hub-rename-${c.id}`} title="Byt namn"
                                            onClick={(e) => { e.stopPropagation(); setRenaming(c.id); setRenameValue(c.title); }}
                                            className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-current transition-opacity">
                                            <Pencil className="w-3 h-3" />
                                        </button>
                                        <button data-testid={`hub-delete-${c.id}`} title="Ta bort"
                                            onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                                            className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity">
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                )}
                                <p className="font-mono text-[9px] text-slate-600 mt-1 uppercase tracking-widest">
                                    {c.project_id ? (projectName(c.project_id) || "Projekt") : "Alla projekt"}
                                </p>
                            </div>
                        ))}
                    </div>
                </aside>

                <main className="flex-1 flex flex-col min-w-0" data-testid="hub-chat-area">
                    {!active ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center max-w-sm px-6">
                                <MessagesSquare className="w-8 h-8 text-slate-700 mx-auto mb-3" />
                                <p className="text-sm text-slate-400">Starta eller välj en konversation för att analysera organisationen med AI:n.</p>
                                <p className="text-xs text-slate-600 mt-2">Du kan t.ex. be AI:n sammanfatta läget, resonera om dammar eller förbereda en presentation för ledningsgruppen.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-border flex items-center gap-3 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <h2 className="text-sm font-semibold text-slate-200 truncate" data-testid="hub-active-title">{active.title}</h2>
                                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-600">
                                        {active.project_id ? `Kopplad till ${projectName(active.project_id) || "projektet"}` : "Kopplad till alla projekt"}
                                    </p>
                                </div>
                                <button data-testid="hub-toggle-drafts-mobile" onClick={() => setShowDraftsMobile((v) => !v)}
                                    className="lg:hidden flex items-center gap-1.5 rounded-lg border border-aidraft/40 bg-aidraft/10 text-aidraft px-2.5 py-2 text-xs font-semibold">
                                    <Sparkles className="w-3.5 h-3.5" /> Utkast ({drafts.length})
                                    {showDraftsMobile ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                                <button data-testid="hub-structure-draft-button" onClick={() => generateDraft("strukturforslag")} disabled={!!generating || streaming}
                                    className="flex items-center gap-2 rounded-lg border border-aidraft/40 bg-aidraft/10 text-aidraft px-3 py-2 text-xs font-semibold hover:bg-aidraft/20 transition-colors disabled:opacity-50">
                                    {generating === "strukturforslag" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Network className="w-3.5 h-3.5" />}
                                    Föreslå ny struktur
                                </button>
                                <button data-testid="hub-presentation-draft-button" onClick={() => generateDraft("presentation")} disabled={!!generating || streaming}
                                    className="flex items-center gap-2 rounded-lg border border-buoy/40 bg-buoy/10 text-buoy px-3 py-2 text-xs font-semibold hover:bg-buoy/20 transition-colors disabled:opacity-50">
                                    {generating === "presentation" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PresentationIcon className="w-3.5 h-3.5" />}
                                    Förbered presentationsutkast
                                </button>
                            </div>
                            {showDraftsMobile && (
                                <div className="lg:hidden border-b border-border p-3 space-y-3 max-h-80 overflow-y-auto panel-scroll" data-testid="hub-drafts-mobile">
                                    {drafts.length === 0 && <p className="text-xs text-slate-600 text-center py-2">Inga utkast ännu.</p>}
                                    {drafts.map((d) => (
                                        <DraftCard key={d.id} draft={d} projectName={projectName(d.project_id)} projects={projects} onStatus={setDraftStatus} onApply={applyStructure} />
                                    ))}
                                </div>
                            )}
                            <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-3" data-testid="hub-messages">
                                {active.messages.length === 0 && (
                                    <p className="text-xs text-slate-600 text-center py-8">Skriv din första instruktion – t.ex. "Starta en ny verksamhetsbild för Lindholm AB: 6 avdelningar, övergripande mål, strategier och syfte", "Föreslå en studie om varför ärenden fastnar" eller "Förbered en presentation för ledningsgruppen".</p>
                                )}
                                {active.messages.map((m, i) => (
                                    <div key={i} data-testid={`hub-message-${i}`}
                                        className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-current/10 border border-current/20 text-slate-200 ml-10" : "bg-deepsea/60 border border-border text-slate-300 mr-10"}`}>
                                        {m.content || (streaming && i === active.messages.length - 1 ? <span className="text-slate-600">tänker …</span> : "")}
                                        {m.link && (
                                            <Link data-testid={`hub-message-link-${i}`}
                                                to={m.link.type === "utbildning" ? "/utbildning" : `/?projekt=${m.link.id}`}
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-current/15 border border-current/40 text-current px-2.5 py-1 text-xs font-semibold hover:bg-current/25 transition-colors">
                                                <ExternalLink className="w-3 h-3" />
                                                {m.link.type === "utbildning" ? "Öppna Utbildning & föreläsning" : `Öppna ${m.link.label || "projektet"}`}
                                            </Link>
                                        )}
                                    </div>
                                ))}
                                <div ref={chatEndRef} />
                            </div>
                            <div className="p-3 border-t border-border">
                                <div className="flex gap-2">
                                    <input data-testid="hub-chat-input"
                                        className="flex-1 bg-abyss/60 border border-border rounded-lg px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-current/60 transition-colors"
                                        placeholder="Instruera AI:n – analysera, föreslå ny organisationsstruktur eller be om presentationsutkast …"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && send()} />
                                    <button data-testid="hub-chat-send" onClick={send} disabled={streaming || !input.trim()}
                                        className="shrink-0 bg-current text-abyss rounded-lg px-3.5 hover:bg-tide transition-colors disabled:opacity-40">
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </main>

                <aside className="hidden lg:flex w-[300px] xl:w-[340px] shrink-0 border-l border-border flex-col min-h-0" data-testid="hub-drafts-panel">
                    <div className="p-3 border-b border-border">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> AI-utkast
                        </p>
                    </div>
                    <div className="flex-1 overflow-y-auto panel-scroll p-3 space-y-3">
                        {drafts.length === 0 && (
                            <p className="text-xs text-slate-600 text-center py-6 px-2">Inga utkast ännu. Be AI:n i chatten – eller använd knapparna ovan – att skapa presentationsutkast eller strukturförslag.</p>
                        )}
                        {drafts.map((d) => (
                            <DraftCard key={d.id} draft={d} projectName={projectName(d.project_id)} projects={projects} onStatus={setDraftStatus} onApply={applyStructure} />
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
}
